import React, { useState } from 'react';
import { FileText, FileCheck, Shield, FileSignature, Download, RefreshCw, AlertCircle, CheckCircle, Loader2, ArrowLeft, ArrowRight, Pause, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { HighTechCard } from './shared';
import { API } from './constants';
import { getToken } from '../../services/tokenService';
import { EcheancierDevis } from './EcheancierDevis';
import { useAuth } from '../../contexts/AuthContext';
import { VisionneuseProtegee } from '../../components/documents/VisionneuseProtegee';

export default function StepRecapDocuments({ ctx }) {
  const {
    loading, documentsGeneres, documentPdfUrl, souscriptionCreee, generationProgress,
    documentScrolled, setDocumentScrolled: setDocumentScrolled,
    ftpeAccepted, ftpeLoading, handleCreateFtpeAdhesion, isFtpeMember,
    handleSaveForLater, savingForLater, setEtape, isNominative,
    genererDocumentsAvecId, ftpeAdhesionId,
    formData,
  } = ctx;

  return (
    <HighTechCard gradient="orange">
      <div className="p-1">
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-t-xl p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-white/20 backdrop-blur"><FileText className="h-6 w-6 text-white" /></div>
            <div>
              <h2 className="text-xl font-bold text-white">Documents contractuels</h2>
              <p className="text-amber-100 text-sm">Consultez le document complet avant signature</p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-4 bg-white rounded-b-xl">
          {loading && !documentsGeneres ? (
            <div className="flex flex-col items-center py-8">
              <div className="w-full max-w-md space-y-6">
                <div className="flex justify-center">
                  <div className="relative">
                    <div className="w-20 h-20 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center shadow-lg shadow-orange-500/30">
                      <FileText className="h-10 w-10 text-white animate-pulse" />
                    </div>
                    <div className="absolute inset-0 rounded-full border-4 border-orange-200 animate-ping opacity-30" />
                  </div>
                </div>
                <div className="text-center">
                  <h3 className="text-lg font-bold text-slate-800 mb-1">Generation des documents en cours</h3>
                  <p className="text-sm text-orange-600 font-medium animate-pulse">{generationProgress.currentLabel || 'Initialisation...'}</p>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-xs font-medium"><span className="text-slate-500">Progression</span><span className="text-orange-600">{generationProgress.percentage}%</span></div>
                  <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 rounded-full transition-all duration-700 ease-out relative" style={{ width: `${generationProgress.percentage}%` }}>
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" />
                    </div>
                  </div>
                </div>
                <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                  {[
                    { pct: 10, label: 'Preparation des donnees' },
                    { pct: 35, label: 'Pack 1 - Attestation + Note juridique' },
                    { pct: 65, label: 'Pack 2 - Conditions Generales + Particulieres' },
                    { pct: 85, label: 'Devis ESS - Simulation tarifaire' },
                    { pct: 100, label: 'Finalisation' }
                  ].map((step, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      {(() => {
                        const pct = generationProgress.percentage;
                        const prevPct = idx > 0 ? [10, 35, 65, 85][idx - 1] : 0;
                        const isComplete = pct >= step.pct;
                        const isInProgress = pct >= prevPct;
                        const circleClass = isComplete ? 'bg-green-500 text-white' : isInProgress ? 'bg-orange-500 text-white animate-pulse' : 'bg-slate-200 text-slate-400';
                        const textClass = isComplete ? 'text-green-700 font-medium' : isInProgress ? 'text-orange-600 font-medium' : 'text-slate-400';
                        return (
                          <>
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all ${circleClass}`}>
                              {isComplete ? '\u2713' : idx + 1}
                            </div>
                            <span className={`text-sm ${textClass}`}>{step.label}</span>
                          </>
                        );
                      })()}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-center text-slate-400">La generation peut prendre jusqu&apos;a 30 secondes</p>
              </div>
            </div>
          ) : !documentsGeneres ? (
            <div className="flex flex-col items-center py-8 space-y-4">
              <AlertCircle className="h-12 w-12 text-amber-500" />
              <p className="text-slate-600">Les documents n&apos;ont pas ete generes automatiquement.</p>
              <Button onClick={async () => { if (souscriptionCreee?.id) await genererDocumentsAvecId(souscriptionCreee.id); }} className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600" data-testid="generate-docs-btn">
                <RefreshCw className="h-5 w-5 mr-2" />Generer les documents
              </Button>
              <p className="text-xs text-slate-400">Souscription ID: {souscriptionCreee?.id || 'Non defini'}</p>
            </div>
          ) : (
            <>
              {/* Document Pack 1 */}
              <DocumentCard
                title="Document RCDS" subtitle="Attestation Garanties solidaire + Note juridique RCDS" pages="~7 pages"
                icon={FileCheck} color="amber" badgeText={documentScrolled ? 'Consulte' : null}
                previewUrl={documentsGeneres?.pack1?.url || documentPdfUrl} downloadName={`RCDS_Attestation_${souscriptionCreee?.raison_sociale || 'document'}.pdf`}
                packKey="pack1" ctx={ctx} />

              {/* Document Pack 2 */}
              <DocumentCard
                title="Document RCDS" subtitle="Conditions Generales + Conditions Particulieres + Attestation" pages="~12 pages"
                icon={Shield} color="blue" badgeText="Conditions" alwaysBadge
                previewUrl={documentsGeneres?.pack2?.url} downloadName={`RCDS_Conditions_${souscriptionCreee?.raison_sociale || 'document'}.pdf`}
                packKey="pack2" ctx={ctx} />

              {/* Devis ESS */}
              {documentsGeneres?.devis_ess && (
                <DocumentCard
                  title="Devis ESS - Simulation" subtitle="Recapitulatif des tarifs et activites selectionnees" pages="1 page"
                  icon={FileText} color="orange" badgeText="APPEL À CONTRIBUTION" alwaysBadge
                  previewUrl={documentsGeneres.devis_ess.url} downloadName={`Devis_ESS_${souscriptionCreee?.raison_sociale || 'document'}.pdf`}
                  packKey="devis" ctx={ctx} />
              )}

              {/* Warnings */}
              {!documentScrolled && (
                <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
                  <div className="flex items-start gap-3"><AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" /><div><p className="font-medium text-amber-800">Lecture obligatoire</p><p className="text-sm text-amber-700 mt-1">Consultez le document RCDS avant de proceder a la signature.</p></div></div>
                </div>
              )}
              {documentScrolled && (
                <div className="p-4 rounded-lg bg-green-50 border border-green-200"><p className="text-sm flex items-center gap-2 text-green-700"><CheckCircle className="h-5 w-5" /><span><strong>Document consulte</strong> - Vous pouvez maintenant proceder a la signature</span></p></div>
              )}

              {/* FTPE Adhesion Section — uniquement si le membre n'a pas d'adhésion FTPE */}
              {!isFtpeMember && (
              <div className="mt-6 p-5 rounded-xl border-2 border-blue-300 bg-blue-50" data-testid="ftpe-adhesion-section">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-blue-600"><FileSignature className="h-5 w-5 text-white" /></div>
                  <div><h3 className="font-bold text-blue-900">Adhesion partenaire FTPE</h3><p className="text-xs text-blue-600">Federation des Tres Petites Entreprises Outremer</p></div>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="p-3 rounded-lg bg-white border border-blue-200"><p className="text-xs text-slate-500">Cotisation annuelle FTPE</p><p className="text-xl font-bold text-blue-700">200,00 &euro;</p></div>
                  <div className="p-3 rounded-lg bg-white border border-blue-200"><p className="text-xs text-slate-500">Paiement</p><p className="text-sm font-medium text-slate-700">Direct a la FTPE</p><p className="text-xs text-slate-500">COPPAM n&apos;encaisse pas</p></div>
                </div>
                <p className="text-xs text-blue-700 mb-3">L&apos;adhesion a la FTPE est obligatoire dans le cadre du parcours cooperatif RCDS BATICO.</p>
                {ftpeAccepted ? (
                  <>
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 border border-green-300 mb-4"><CheckCircle className="h-5 w-5 text-green-600" /><span className="text-green-700 font-medium text-sm">Adhesion FTPE creee</span></div>
                    {/* Attestation FTPE DRAFT - document téléchargeable */}
                    {ftpeAdhesionId && (
                      <DocumentCard
                        title="Attestation FTPE DRAFT"
                        subtitle="Attestation d'adhesion a la FTPE (brouillon)"
                        pages="1 page"
                        icon={FileSignature}
                        color="blue"
                        badgeText="DRAFT"
                        alwaysBadge
                        previewUrl={`${API}/ftpe-adhesion/workflow/${ftpeAdhesionId}/attestation-draft${formData?.siret ? `?siret=${encodeURIComponent(formData.siret)}` : ''}`}
                        downloadName={`Attestation_FTPE_DRAFT_${souscriptionCreee?.raison_sociale || 'document'}.pdf`}
                        packKey="ftpe-attestation"
                        ctx={ctx}
                      />
                    )}
                  </>
                ) : (
                  <Button onClick={handleCreateFtpeAdhesion} disabled={ftpeLoading} className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white" data-testid="ftpe-create-btn">
                    {ftpeLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileSignature className="mr-2 h-4 w-4" />}Creer mon adhesion FTPE
                  </Button>
                )}
              </div>
              )}

              {/* V36.5 — Bloc Paiement échelonné (cohérent avec PDF DEVIS ESS) */}
              <EcheancierDevis souscriptionId={souscriptionCreee?.id} />

              {/* Navigation */}
              <div className="flex justify-between pt-4">
                <Button onClick={() => setEtape(isNominative ? 3 : 2)} variant="outline" className="h-12"><ArrowLeft className="mr-2 h-5 w-5" />Retour</Button>
                <div className="flex gap-3">
                  <Button onClick={handleSaveForLater} variant="outline" disabled={savingForLater} className="h-12 px-4 border-amber-400 text-amber-700 hover:bg-amber-50">
                    {savingForLater ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Pause className="mr-2 h-5 w-5" />Sauvegarder</>}
                  </Button>
                  <Button onClick={() => setEtape(5)} disabled={!documentScrolled || (!isFtpeMember && !ftpeAccepted)} className="h-12 px-8 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 disabled:opacity-50">
                    Proceder a la signature<ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </HighTechCard>
  );
}

// Sub-component for document cards
function DocumentCard({ title, subtitle, pages, icon: Icon, color, badgeText, alwaysBadge, previewUrl, downloadName, packKey, ctx }) {
  const { setDocumentScrolled, documentScrolled } = ctx;
  const [downloading, setDownloading] = useState(false);
  const [downloadDone, setDownloadDone] = useState(false);
  const [progress, setProgress] = useState(0);
  const [protege, setProtege] = useState(false);
  const { user } = useAuth() || {};
  const lectureSeule = !['admin', 'super_admin'].includes((user?.role || '').toLowerCase());

  const colorMap = { amber: 'from-amber-500 to-orange-500', blue: 'from-blue-500 to-indigo-600', orange: 'from-orange-500 to-amber-500' };
  const borderMap = { amber: documentScrolled ? 'bg-green-50 border-green-400' : 'bg-gradient-to-br from-amber-50 to-orange-50 border-amber-300', blue: 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-300', orange: 'bg-gradient-to-br from-orange-50 to-amber-50 border-orange-300' };
  const btnMap = { amber: 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600', blue: 'border-blue-300 text-blue-700 hover:bg-blue-50', orange: 'border-orange-300 text-orange-700 hover:bg-orange-50' };

  const buildUrl = (preview = false) => {
    if (!previewUrl) return '';
    const token = getToken();
    const sep = previewUrl.includes('?') ? '&' : '?';
    return `${previewUrl}${sep}token=${encodeURIComponent(token)}${preview ? '&preview=true' : ''}`;
  };

  const handlePreview = () => {
    if (!previewUrl) return;
    if (lectureSeule) setProtege(true);
    else window.open(buildUrl(true), '_blank');
    if (color === 'amber') setDocumentScrolled(true);
  };

  const handleDownload = async () => {
    if (!previewUrl || downloading) return;
    setDownloading(true);
    setDownloadDone(false);
    setProgress(10);

    const fullUrl = buildUrl(false);
    try {
      // Utiliser XMLHttpRequest pour avoir la progression
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', fullUrl, true);
        xhr.responseType = 'blob';
        xhr.onprogress = (e) => {
          if (e.lengthComputable) {
            setProgress(Math.round((e.loaded / e.total) * 90) + 10);
          } else {
            setProgress(prev => Math.min(prev + 15, 85));
          }
        };
        xhr.onload = () => {
          if (xhr.status === 200) {
            setProgress(100);
            const blob = xhr.response;
            // Sécurité : si on attend un PDF mais reçoit du JSON → erreur backend
            if (blob && blob.type && blob.type.includes('application/json')) {
              blob.text().then(txt => {
                let detail = 'Erreur génération document';
                try { const j = JSON.parse(txt); if (j?.detail) detail = j.detail; } catch { /* noop */ }
                reject(new Error(detail));
              }).catch(() => reject(new Error('Erreur génération document')));
              return;
            }
            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = downloadName || 'document.pdf';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
            resolve();
          } else {
            // Essayer de lire le détail d'erreur (JSON) plutôt que rejeter avec HTTP nu
            const blob = xhr.response;
            if (blob && blob.type && blob.type.includes('application/json')) {
              blob.text().then(txt => {
                let detail = `HTTP ${xhr.status}`;
                try { const j = JSON.parse(txt); if (j?.detail) detail = j.detail; } catch { /* noop */ }
                reject(new Error(detail));
              }).catch(() => reject(new Error(`HTTP ${xhr.status}`)));
            } else {
              reject(new Error(`HTTP ${xhr.status}`));
            }
          }
        };
        xhr.onerror = () => reject(new Error('Erreur reseau'));
        xhr.ontimeout = () => reject(new Error('Timeout'));
        xhr.timeout = 120000;
        xhr.send();
      });

      setDownloadDone(true);
      if (color === 'amber') setDocumentScrolled(true);
      setTimeout(() => { setDownloadDone(false); setProgress(0); }, 3000);
    } catch (err) {
      console.error('Download error:', err);
      // Afficher un toast clair plutôt que d'ouvrir une page JSON 500 dans un nouvel onglet
      // (qui peut causer un "Script error" si rrweb/intercepteurs essaient de cloner la response)
      const msg = err?.message ? `Erreur : ${err.message}` : 'Erreur lors du téléchargement';
      toast.error(msg);
      setProgress(0);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className={`p-5 rounded-xl border-2 transition-all ${borderMap[color]}`} data-testid={`doc-card-${packKey}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-xl bg-gradient-to-br ${colorMap[color]} shadow-lg`}><Icon className="h-6 w-6 text-white" /></div>
          <div><h4 className="font-bold text-slate-800 text-lg">{title}</h4><p className="text-sm text-slate-500">{subtitle}</p><p className="text-xs text-slate-400 mt-1">{pages}</p></div>
        </div>
        {badgeText && (alwaysBadge || documentScrolled) && <Badge className={`${color === 'amber' ? 'bg-green-500' : color === 'blue' ? 'bg-blue-500' : 'bg-orange-500'} text-white`}>{badgeText}</Badge>}
      </div>

      {/* Barre de progression */}
      {downloading && (
        <div className="mb-3" data-testid={`${packKey}-progress`}>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium text-slate-600">Preparation du document...</span>
            <span className="text-xs font-semibold text-slate-700">{progress}%</span>
          </div>
          <div className="w-full bg-slate-200 rounded-full h-1.5 overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 transition-all duration-300 ease-out" style={{ width: `${progress}%` }} />
          </div>
        </div>
      )}

      <div className="flex gap-3">
        <Button onClick={handlePreview} disabled={!previewUrl || downloading} className={`flex-1 h-${color === 'amber' ? '12' : '10'} ${color === 'amber' ? btnMap[color] : ''}`} variant={color !== 'amber' ? 'outline' : 'default'} data-testid={`${packKey}-preview-btn`}>
          <ExternalLink className={`h-${color === 'amber' ? '5' : '4'} w-${color === 'amber' ? '5' : '4'} mr-2`} />Consulter
        </Button>
        {!lectureSeule && <Button variant="outline" className={`h-${color === 'amber' ? '12' : '10'} px-${color === 'amber' ? '6' : '4'} min-w-[160px] ${color !== 'amber' ? btnMap[color] : ''} ${downloadDone ? '!border-green-400 !text-green-600' : ''}`} disabled={!previewUrl || downloading} onClick={handleDownload} data-testid={`${packKey}-download-btn`}>
          {downloading ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Telechargement...</>
          ) : downloadDone ? (
            <><CheckCircle className="h-4 w-4 mr-2" />Telecharge !</>
          ) : (
            <><Download className="h-4 w-4 mr-2" />Telecharger</>
          )}
        </Button>}
      </div>
      {lectureSeule && <p className="mt-2 text-xs text-amber-700" data-testid={`${packKey}-lecture-seule`}>Document provisoire, sans bloc de signature — consultation uniquement jusqu&apos;à la validation du dossier.</p>}
      {protege && <VisionneuseProtegee url={`${previewUrl}${previewUrl.includes('?') ? '&' : '?'}preview=true`} titre={`${title} — ${subtitle}`} onClose={() => setProtege(false)} />}
    </div>
  );
}
